import React from "react";
import { Box, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Printable } from "@components";
import AnalyticsKpiCards from "./AnalyticsKpiCards";
import CategoryDistributionChart from "./CategoryDistributionChart";
import DistributorSalesChart from "./DistributorSalesChart";
import OrganizationTestCountsTable from "./OrganizationTestCountsTable";
import {
  selectCategoryDistribution,
  selectDemoTestTotals,
  selectDistributorOrgTestCounts,
  selectDistributorSales,
} from "@store/analytics/analyticsSelectors";
import { SalesMetric, TimePeriod } from "types/analyticsTypes";

interface AnalyticsReportProps {
  period: TimePeriod;
  rangeActive: boolean;
  startDate?: string;
  endDate?: string;
}

const AnalyticsReport: React.FC<AnalyticsReportProps> = ({
  period,
  rangeActive,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation();
  const salesData = useSelector(selectDistributorSales);
  const testCounts = useSelector(selectDistributorOrgTestCounts);
  const categoryDistribution = useSelector(selectCategoryDistribution);
  const demoTotals = useSelector(selectDemoTestTotals);

  return (
    <Box pos="absolute" top={0} left={-10000} w={1500}>
      <Printable showPrintButton={false}>
        <Printable.Section>
          <Box>
            <Group justify="space-between" mb="sm">
              <Title order={2}>{t("TestDashboard.analytics")}</Title>
              <Text size="sm" c="dimmed">
                {t("analytics.export.dateRange")}:{" "}
                {rangeActive ? `${startDate} / ${endDate}` : t("analytics.kpi.allTime")}
              </Text>
            </Group>
            <AnalyticsKpiCards
              testCounts={testCounts}
              sales={salesData}
              demoTotals={demoTotals}
              period={rangeActive ? TimePeriod.RANGE : period}
            />
            <Stack gap="md" mt="md">
              <Paper shadow="xs" p="md" withBorder bg="var(--oculera-card-bg)">
                <Title order={3} mb="md">
                  {t("analytics.sales.newlyCreatedAccounts.title")}
                </Title>
                <DistributorSalesChart
                  data={salesData}
                  metric={SalesMetric.ACCOUNTS}
                  title={t("analytics.kpi.totalAccountCount")}
                />
                <DistributorSalesChart
                  data={salesData}
                  metric={SalesMetric.DEVICES}
                  title={t("analytics.kpi.uniqueDevicesRegistered")}
                />
              </Paper>
              <Paper shadow="xs" p="md" withBorder bg="var(--oculera-card-bg)">
                <Title order={3} mb="md">
                  {t("analytics.category.title")}
                </Title>
                <CategoryDistributionChart data={categoryDistribution} />
              </Paper>
            </Stack>
          </Box>
        </Printable.Section>
        <Printable.Section breakRule="auto">
          <Paper shadow="xs" p="md" withBorder bg="var(--oculera-card-bg)">
            <OrganizationTestCountsTable
              data={testCounts}
              period={period}
              onPeriodChange={() => undefined}
              rangeActive={rangeActive}
              startDate={startDate}
              endDate={endDate}
              expanded
            />
          </Paper>
        </Printable.Section>
      </Printable>
    </Box>
  );
};

export default AnalyticsReport;
